import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ProjectSide, RawQuoteRecord } from "@/v2Projects/types";
import type {
  CreateProjectQuoteRequest,
  QuotePaymentStatus,
} from "@shopexperts/legacy-api";

export function useProjectQuotes(
  projectId: Ref<number>,
  side: Ref<ProjectSide>,
) {
  const api = useApi();
  const queryClient = useQueryClient();

  const projectsApi = computed(() =>
    side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2,
  );

  const quotesQueryKey = computed(() => [
    "v2projects",
    "quotes",
    projectId.value,
  ]);

  function invalidateRelated() {
    queryClient.invalidateQueries({ queryKey: quotesQueryKey.value });
    queryClient.invalidateQueries({
      queryKey: ["v2projects", "detail", projectId.value],
    });
  }

  const configQuery = useQuery({
    queryKey: ["v2projects", "config"],
    queryFn: async () => await projectsApi.value.getConfig({}),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const listQuery = useQuery<RawQuoteRecord[]>({
    queryKey: quotesQueryKey,
    queryFn: async () =>
      (await projectsApi.value.listQuotes({
        params: { projectId: projectId.value },
      })) as RawQuoteRecord[],
    enabled: computed(() => projectId.value > 0),
    staleTime: 15_000,
  });

  const createQuote = useMutation<unknown, Error, CreateProjectQuoteRequest>({
    mutationFn: (body) =>
      projectsApi.value.createQuote({
        params: { projectId: projectId.value },
        body,
      }),
    onSuccess: invalidateRelated,
  });

  const cancelQuote = useMutation({
    mutationFn: (input: { quoteId: number; actingOrgId: number }) =>
      projectsApi.value.cancelQuote({
        params: { projectId: projectId.value, quoteId: input.quoteId },
        body: { acting_org_id: input.actingOrgId },
      }),
    onSuccess: invalidateRelated,
  });

  const rejectQuote = useMutation({
    mutationFn: (input: { quoteId: number; actingOrgId: number }) =>
      projectsApi.value.rejectQuote({
        params: { projectId: projectId.value, quoteId: input.quoteId },
        body: { acting_org_id: input.actingOrgId },
      }),
    onSuccess: invalidateRelated,
  });

  const viewQuote = useMutation({
    mutationFn: (input: { quoteId: number; actingOrgId: number }) =>
      projectsApi.value.viewQuote({
        params: { projectId: projectId.value, quoteId: input.quoteId },
        body: { acting_org_id: input.actingOrgId },
      }),
    onSuccess: invalidateRelated,
  });

  const updateQuotePaymentStatus = useMutation({
    mutationFn: (input: {
      quoteId: number;
      actingOrgId: number;
      paymentStatus: QuotePaymentStatus;
    }) =>
      projectsApi.value.updateQuotePaymentStatus({
        params: { projectId: projectId.value, quoteId: input.quoteId },
        body: {
          acting_org_id: input.actingOrgId,
          payment_status: input.paymentStatus,
        },
      }),
    onSuccess: invalidateRelated,
  });

  return {
    quotes: computed(() => listQuery.data.value ?? []),
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: listQuery.refetch,
    config: computed(() => configQuery.data.value ?? null),
    quoteCurrencies: computed(
      () => configQuery.data.value?.quote_payments.supported_currencies ?? ["USD"],
    ),
    defaultQuoteCurrency: computed(
      () => configQuery.data.value?.quote_payments.default_currency ?? "USD",
    ),
    createQuote,
    cancelQuote,
    rejectQuote,
    viewQuote,
    updateQuotePaymentStatus,
  };
}
